import React from "react";

import { useMutation } from "@apollo/client";
import Button from "@material-ui/core/Button";
import PropTypes from "prop-types";

import RbButtonGroup from "components/RbButtonGroup";
import RbGrid from "components/RbGrid";
import RbNumberField from "components/RbNumberField";
import RbTextField from "components/RbTextField";
import FieldsProvider from "fields/components/FieldsProvider";
import useFields from "fields/hooks/useFields";
import {
  updateCarMutation,
  createCarMutation,
  carListQuery,
} from "graphql/car";
import useSnackbar from "hooks/useSnackbar";
import { convertValuesToInput } from "utils/graphqlUtils";

function CarForm(props) {
  const { car, onCancel, onSave } = props;

  const modelRef = React.useRef();

  const { openSnackbar } = useSnackbar();

  const { fields, handleChange, reset, valid } = useFields({
    initialValues: car,
  });

  const [createCar] = useMutation(createCarMutation, {
    onCompleted() {
      openSnackbar({
        message: "Car created successfully!",
      });
      onSave();
    },
    update(cache, result) {
      const { carList } = cache.readQuery({ query: carListQuery });
      cache.writeQuery({
        query: carListQuery,
        data: {
          carList: [result.data.createCar, ...carList],
        },
      });
    },
  });

  const [updateCar] = useMutation(updateCarMutation, {
    onCompleted() {
      openSnackbar({
        message: "Car updated successfully!",
      });
      onSave();
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    const variables = { input: convertValuesToInput(fields.values) };
    if (car.id) {
      updateCar({ variables });
    } else {
      createCar({ variables });
    }
  };

  React.useEffect(() => {
    modelRef.current.focus();
    reset();
  }, [car, reset]);

  return (
    <form noValidate onSubmit={handleSubmit}>
      <FieldsProvider fields={fields} onChange={handleChange}>
        <RbGrid container p={5} columnGap={4} rowGap={4}>
          <RbGrid item xs={12} lg={4}>
            <RbTextField
              inputRef={modelRef}
              fieldName="model"
              label="Model"
              required
            />
          </RbGrid>
          <RbGrid item xs={12} lg={3}>
            <RbTextField fieldName="color" label="Color" />
          </RbGrid>
          <RbGrid item xs={6} lg={2}>
            <RbNumberField fieldName="year" label="Year" required />
          </RbGrid>
          <RbGrid item xs={6} lg={2}>
            <RbNumberField fieldName="price" label="Price" />
          </RbGrid>
          <RbGrid item xs={12}>
            <RbButtonGroup gap={4}>
              <Button variant="contained" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!valid}
              >
                Save
              </Button>
            </RbButtonGroup>
          </RbGrid>
        </RbGrid>
      </FieldsProvider>
    </form>
  );
}

CarForm.propTypes = {
  car: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default React.memo(CarForm);
